import { z } from 'zod';
import { isValidGtin } from './barcode.js';
import { CursorPageQuerySchema, IsoDateTimeSchema, UuidSchema, optionalText, pageSchema } from './common.js';

/** The same rule as `GtinSchema` of the clinic's items (inventory.ts imports this file, not the reverse). */
const GtinSchema = z
  .string()
  .trim()
  .refine(isValidGtin, { message: 'بارکد کالا (GTIN) معتبر نیست.' })
  .transform((v) => v.padStart(14, '0'));

/**
 * The central GTIN catalog (ADR-055). A clinic's GTIN works for that clinic at once and goes to the
 * platform's review queue; only an approved GTIN is used by every clinic. One catalog product is one
 * trade item (a volume, a dose, with or without lidocaine, a pack size each have their own GTIN), so
 * products are never merged by name. A valid check digit is a valid shape, never authenticity.
 */

/** Fixed categories of the catalog (employer 2026-10-22: today's suggestion groups and «سایر»). */
export const CATALOG_CATEGORIES = [
  'toxin',
  'filler',
  'body_filler',
  'skin_booster',
  'polynucleotide',
  'collagen_stimulator',
  'mesotherapy',
  'hair_mesotherapy',
  'fat_dissolver',
  'hyaluronidase',
  'thread',
  'prp',
  'autologous',
  'anesthetic',
  'consumable',
  'other',
] as const;
export type CatalogCategory = (typeof CATALOG_CATEGORIES)[number];

export const CATALOG_CATEGORY_LABELS: Readonly<Record<CatalogCategory, string>> = {
  toxin: 'بوتاکس',
  filler: 'فیلر',
  body_filler: 'فیلر بدن',
  skin_booster: 'اسکین‌بوستر',
  polynucleotide: 'پلی‌نوکلئوتید',
  collagen_stimulator: 'محرک کلاژن',
  mesotherapy: 'مزوتراپی',
  hair_mesotherapy: 'مزوتراپی مو',
  fat_dissolver: 'چربی‌سوز تزریقی',
  hyaluronidase: 'هیالورونیداز',
  thread: 'نخ',
  prp: 'PRP',
  autologous: 'فرآورده‌ی اتولوگ',
  anesthetic: 'بی‌حسی',
  consumable: 'مصرفی',
  other: 'سایر',
};

/** withdrawn: the clinic's item no longer carries the GTIN (changed or cleared) while it waited. */
export const CATALOG_SUBMISSION_STATUSES = ['pending', 'approved', 'rejected', 'withdrawn'] as const;
export type CatalogSubmissionStatus = (typeof CATALOG_SUBMISSION_STATUSES)[number];

export const CATALOG_PRODUCT_STATUSES = ['active', 'retired'] as const;
export type CatalogProductStatus = (typeof CATALOG_PRODUCT_STATUSES)[number];

export const CATALOG_EVENT_ACTIONS = ['approved', 'linked', 'rejected', 'created', 'edited', 'retired', 'restored', 'merged'] as const;
export type CatalogEventAction = (typeof CATALOG_EVENT_ACTIONS)[number];

export const DEFAULT_ITEM_STATUSES = ['active', 'removed'] as const;
export type DefaultItemStatus = (typeof DEFAULT_ITEM_STATUSES)[number];

export const DEFAULT_ITEM_SOURCES = ['seed', 'platform', 'catalog', 'clinic_request'] as const;
export type DefaultItemSource = (typeof DEFAULT_ITEM_SOURCES)[number];

export const DEFAULT_ITEM_REMOVED_REASONS = ['manual', 'replaced'] as const;
export type DefaultItemRemovedReason = (typeof DEFAULT_ITEM_REMOVED_REASONS)[number];

export const DEFAULT_ITEM_REQUEST_STATUSES = ['pending', 'added', 'dismissed', 'withdrawn'] as const;
export type DefaultItemRequestStatus = (typeof DEFAULT_ITEM_REQUEST_STATUSES)[number];

export const DEFAULT_ITEM_REQUEST_QUEUE_STATUSES = ['pending', 'dismissed'] as const;
export type DefaultItemRequestQueueStatus = (typeof DEFAULT_ITEM_REQUEST_QUEUE_STATUSES)[number];

export const DEFAULT_ITEM_REQUEST_SOURCES = ['item_saved', 'backfill'] as const;
export type DefaultItemRequestSource = (typeof DEFAULT_ITEM_REQUEST_SOURCES)[number];

export const DEFAULT_ITEM_EVENT_ACTIONS = [
  'added',
  'edited',
  'removed',
  'restored',
  'replaced',
  'request_added',
  'request_dismissed',
] as const;
export type DefaultItemEventAction = (typeof DEFAULT_ITEM_EVENT_ACTIONS)[number];

export const DEFAULT_ITEM_GTIN_FILTERS = ['with', 'without'] as const;
export type DefaultItemGtinFilter = (typeof DEFAULT_ITEM_GTIN_FILTERS)[number];

export const ReplacedDefaultItemRefSchema = z.object({
  id: UuidSchema,
  nameFa: z.string(),
});
export type ReplacedDefaultItemRef = z.infer<typeof ReplacedDefaultItemRefSchema>;

export const ReplaceDefaultItemIdsSchema = z
  .array(UuidSchema)
  .max(20, { message: 'حداکثر ۲۰ مورد.' })
  .optional();

const requiredText = (max: number, message: string) =>
  z
    .string()
    .trim()
    .min(1, { message })
    .max(max, { message: `حداکثر ${max} نویسه.` });

// ---- the catalog ----

export const CatalogProductSchema = z.object({
  id: UuidSchema,
  /** The product's GTINs (14 digits); usually one. */
  gtins: z.array(z.string()),
  brand: z.string(),
  model: z.string(),
  nameFa: z.string(),
  nameEn: z.string().nullable(),
  category: z.enum(CATALOG_CATEGORIES),
  /** The unit stock is counted in (ویال، سرنگ، …). */
  unit: z.string(),
  /** Volume or dose as printed, e.g. «1 ml» or «500 U». */
  content: z.string().nullable(),
  /** null = not stated. */
  hasLidocaine: z.boolean().nullable(),
  packType: z.string().nullable(),
  packCount: z.number().int().nullable(),
  status: z.enum(CATALOG_PRODUCT_STATUSES),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type CatalogProduct = z.infer<typeof CatalogProductSchema>;

/** What the platform admin approves or edits. */
export const CatalogProductInputSchema = z.object({
  brand: requiredText(80, 'برند را بنویسید.'),
  model: requiredText(120, 'مدل دقیق را بنویسید.'),
  nameFa: requiredText(120, 'نام فارسی را بنویسید.'),
  nameEn: optionalText(120),
  category: z.enum(CATALOG_CATEGORIES, { message: 'دسته را انتخاب کنید.' }),
  unit: requiredText(20, 'واحد را بنویسید (مثلاً ویال).'),
  content: optionalText(40),
  hasLidocaine: z.boolean().nullish().transform((v) => v ?? null),
  packType: optionalText(40),
  packCount: z
    .number()
    .int()
    .min(1, { message: 'تعداد در بسته دست‌کم ۱ است.' })
    .max(1000, { message: 'تعداد در بسته حداکثر ۱۰۰۰ است.' })
    .nullish()
    .transform((v) => v ?? null),
});
export type CatalogProductInput = z.input<typeof CatalogProductInputSchema>;

export const CatalogProductCreateInputSchema = CatalogProductInputSchema.extend({
  gtin: GtinSchema,
  replaceDefaultItemIds: ReplaceDefaultItemIdsSchema,
});
export type CatalogProductCreateInput = z.input<typeof CatalogProductCreateInputSchema>;

export const CatalogReasonInputSchema = z.object({
  reason: requiredText(300, 'دلیل را بنویسید.').refine((v) => v.length >= 3, { message: 'دلیل دست‌کم ۳ نویسه است.' }),
});
export type CatalogReasonInput = z.input<typeof CatalogReasonInputSchema>;

export const CatalogMergeInputSchema = CatalogReasonInputSchema.extend({ targetId: UuidSchema });
export type CatalogMergeInput = z.input<typeof CatalogMergeInputSchema>;

/** Approving a GTIN: a new product with these details, or the GTIN joins an existing product. */
export const CatalogApproveInputSchema = z.union([
  z.object({ product: CatalogProductInputSchema, replaceDefaultItemIds: ReplaceDefaultItemIdsSchema }),
  z.object({
    productId: UuidSchema,
    reason: CatalogReasonInputSchema.shape.reason,
    replaceDefaultItemIds: ReplaceDefaultItemIdsSchema,
  }),
]);
export type CatalogApproveInput = z.input<typeof CatalogApproveInputSchema>;

export const CatalogProductListQuerySchema = CursorPageQuerySchema.extend({
  q: z.string().trim().max(100).optional(),
  status: z.enum(CATALOG_PRODUCT_STATUSES).optional(),
  category: z.enum(CATALOG_CATEGORIES).optional(),
});
export type CatalogProductListQuery = z.infer<typeof CatalogProductListQuerySchema>;
export const CatalogProductListResponseSchema = pageSchema(CatalogProductSchema);
export type CatalogProductListResponse = z.infer<typeof CatalogProductListResponseSchema>;

export const CatalogEventSchema = z.object({
  id: UuidSchema,
  action: z.enum(CATALOG_EVENT_ACTIONS),
  gtin: z.string().nullable(),
  productId: UuidSchema.nullable(),
  reason: z.string().nullable(),
  /** Details before and after an edit. */
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  actorName: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type CatalogEvent = z.infer<typeof CatalogEventSchema>;

export const CatalogProductDetailSchema = z.object({
  product: CatalogProductSchema,
  history: z.array(CatalogEventSchema),
  replacedDefaultItems: z.array(ReplacedDefaultItemRefSchema).optional(),
});
export type CatalogProductDetail = z.infer<typeof CatalogProductDetailSchema>;

// ---- a clinic's proposal ----

/** What a clinic proposed for a GTIN: its item's fields and the optional details. */
export const CatalogProposalSchema = z.object({
  nameFa: z.string(),
  nameEn: z.string().nullable(),
  /** The clinic's own category text. */
  categoryText: z.string().nullable(),
  unit: z.string(),
  brand: z.string().nullable(),
  model: z.string().nullable(),
  content: z.string().nullable(),
  hasLidocaine: z.boolean().nullable(),
  packType: z.string().nullable(),
  packCount: z.number().int().nullable(),
});
export type CatalogProposal = z.infer<typeof CatalogProposalSchema>;

/** The details a clinic may add to its pending proposal. */
export const CatalogDetailsInputSchema = z.object({
  brand: optionalText(80),
  model: optionalText(120),
  content: optionalText(40),
  hasLidocaine: CatalogProductInputSchema.shape.hasLidocaine,
  packType: optionalText(40),
  packCount: CatalogProductInputSchema.shape.packCount,
});
export type CatalogDetailsInput = z.input<typeof CatalogDetailsInputSchema>;

/** A clinic's own proposal for one GTIN, as that clinic sees it. */
export const ClinicCatalogSubmissionSchema = z.object({
  id: UuidSchema,
  gtin: z.string(),
  status: z.enum(CATALOG_SUBMISSION_STATUSES),
  proposal: CatalogProposalSchema,
  rejectReason: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  decidedAt: IsoDateTimeSchema.nullable(),
});
export type ClinicCatalogSubmission = z.infer<typeof ClinicCatalogSubmissionSchema>;

export const CatalogLookupQuerySchema = z.object({ gtin: GtinSchema });
export const CatalogLookupResponseSchema = z.object({
  /** The approved product of this GTIN; null when the catalog does not have it (yet). */
  product: CatalogProductSchema.nullable(),
  /** This clinic's latest proposal for the GTIN; never another clinic's. */
  submission: ClinicCatalogSubmissionSchema.nullable(),
});
export type CatalogLookupResponse = z.infer<typeof CatalogLookupResponseSchema>;

// ---- the review queue (platform) ----

export const CatalogReviewListQuerySchema = CursorPageQuerySchema.extend({
  status: z.enum(CATALOG_SUBMISSION_STATUSES).default('pending'),
  q: z.string().trim().max(100).optional(),
});
export type CatalogReviewListQuery = z.infer<typeof CatalogReviewListQuerySchema>;

/** One GTIN in the queue: every clinic's proposal of it is one row. */
export const CatalogReviewSchema = z.object({
  /** The first proposal's id (the cursor). */
  id: UuidSchema,
  gtin: z.string(),
  status: z.enum(CATALOG_SUBMISSION_STATUSES),
  /** The most recent proposal's details. */
  proposal: CatalogProposalSchema,
  clinicCount: z.number().int(),
  firstAt: IsoDateTimeSchema,
  lastAt: IsoDateTimeSchema,
  productId: UuidSchema.nullable(),
  /** Catalog products of the same brand and model: a hint to check, never merged by itself. */
  possibleDuplicates: z.number().int(),
});
export type CatalogReview = z.infer<typeof CatalogReviewSchema>;
export const CatalogReviewListResponseSchema = pageSchema(CatalogReviewSchema);
export type CatalogReviewListResponse = z.infer<typeof CatalogReviewListResponseSchema>;

export const CatalogReviewSubmissionSchema = z.object({
  id: UuidSchema,
  status: z.enum(CATALOG_SUBMISSION_STATUSES),
  proposal: CatalogProposalSchema,
  rejectReason: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  decidedAt: IsoDateTimeSchema.nullable(),
  /** The proposing clinic: only with `catalog.sources`. */
  clinic: z.object({ id: UuidSchema, name: z.string() }).nullable(),
});
export type CatalogReviewSubmission = z.infer<typeof CatalogReviewSubmissionSchema>;

export const CatalogReviewDetailSchema = z.object({
  gtin: z.string(),
  status: z.enum(CATALOG_SUBMISSION_STATUSES).nullable(),
  product: CatalogProductSchema.nullable(),
  submissions: z.array(CatalogReviewSubmissionSchema),
  possibleDuplicates: z.array(CatalogProductSchema),
  history: z.array(CatalogEventSchema),
  replacedDefaultItems: z.array(ReplacedDefaultItemRefSchema).optional(),
});
export type CatalogReviewDetail = z.infer<typeof CatalogReviewDetailSchema>;

export const CatalogCountsSchema = z.object({
  pending: z.number().int(),
  approvedGtins: z.number().int(),
  defaultItemRequests: z.number().int(),
});
export type CatalogCounts = z.infer<typeof CatalogCountsSchema>;

// ---- the default item list (ADR-057) ----

/** One active entry in the default list as a clinic reads it (to prefill ItemForm). */
export const ClinicDefaultItemSchema = z.object({
  id: UuidSchema,
  nameFa: z.string(),
  nameEn: z.string().nullable(),
  category: z.enum(CATALOG_CATEGORIES),
  unit: z.string(),
  gtin: z.string().nullable(),
});
export type ClinicDefaultItem = z.infer<typeof ClinicDefaultItemSchema>;

export const ClinicDefaultItemListResponseSchema = z.object({
  items: z.array(ClinicDefaultItemSchema),
});
export type ClinicDefaultItemListResponse = z.infer<typeof ClinicDefaultItemListResponseSchema>;

/** One entry in the platform's default item list (GTIN product reference or non-GTIN row). */
export const DefaultItemSchema = z.object({
  id: UuidSchema,
  productId: UuidSchema.nullable(),
  nameFa: z.string(),
  nameEn: z.string().nullable(),
  category: z.enum(CATALOG_CATEGORIES),
  unit: z.string(),
  gtins: z.array(z.string()),
  status: z.enum(DEFAULT_ITEM_STATUSES),
  source: z.enum(DEFAULT_ITEM_SOURCES),
  removedReason: z.enum(DEFAULT_ITEM_REMOVED_REASONS).nullable(),
  replacedByProduct: ReplacedDefaultItemRefSchema.nullable(),
  removedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type DefaultItem = z.infer<typeof DefaultItemSchema>;

export const DefaultItemInputSchema = z.object({
  nameFa: requiredText(120, 'نام فارسی را بنویسید.'),
  nameEn: optionalText(120),
  category: z.enum(CATALOG_CATEGORIES, { message: 'دسته را انتخاب کنید.' }),
  unit: requiredText(20, 'واحد را بنویسید (مثلاً ویال).'),
});
export type DefaultItemInput = z.input<typeof DefaultItemInputSchema>;

export const DefaultItemRemoveInputSchema = z.object({
  reason: optionalText(300).refine((v) => v === null || v.length >= 3, {
    message: 'دلیل دست‌کم ۳ نویسه است.',
  }),
});
export type DefaultItemRemoveInput = z.input<typeof DefaultItemRemoveInputSchema>;

export const DefaultItemListQuerySchema = CursorPageQuerySchema.extend({
  after: UuidSchema.optional(),
  status: z.enum(DEFAULT_ITEM_STATUSES).default('active'),
  q: z.string().trim().max(100).optional(),
  category: z.enum(CATALOG_CATEGORIES).optional(),
  gtin: z.enum(DEFAULT_ITEM_GTIN_FILTERS).optional(),
});
export type DefaultItemListQuery = z.infer<typeof DefaultItemListQuerySchema>;

export const DefaultItemListResponseSchema = pageSchema(DefaultItemSchema);
export type DefaultItemListResponse = z.infer<typeof DefaultItemListResponseSchema>;

export const DefaultItemEventSchema = z.object({
  id: UuidSchema,
  action: z.enum(DEFAULT_ITEM_EVENT_ACTIONS),
  defaultItemId: UuidSchema.nullable(),
  productId: UuidSchema.nullable(),
  nameKey: z.string().nullable(),
  reason: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  requestCount: z.number().int(),
  actorName: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type DefaultItemEvent = z.infer<typeof DefaultItemEventSchema>;

export const DefaultItemDetailSchema = z.object({
  item: DefaultItemSchema,
  history: z.array(DefaultItemEventSchema),
});
export type DefaultItemDetail = z.infer<typeof DefaultItemDetailSchema>;

export const DefaultItemMatchesQuerySchema = z.object({
  nameFa: z.string().trim().max(120).optional(),
  nameEn: z.string().trim().max(120).optional(),
  brand: z.string().trim().max(80).optional(),
  model: z.string().trim().max(120).optional(),
});
export type DefaultItemMatchesQuery = z.infer<typeof DefaultItemMatchesQuerySchema>;

export const DefaultItemMatchesResponseSchema = z.object({
  exact: z.array(DefaultItemSchema),
  near: z.array(DefaultItemSchema),
});
export type DefaultItemMatchesResponse = z.infer<typeof DefaultItemMatchesResponseSchema>;

export const DefaultItemRequestGroupSchema = z.object({
  nameKey: z.string(),
  nameFa: z.string(),
  nameEn: z.string().nullable(),
  categoryText: z.string().nullable(),
  unit: z.string(),
  gtins: z.array(z.string()),
  clinicCount: z.number().int(),
  requestCount: z.number().int(),
  lastAt: IsoDateTimeSchema,
  priorDismissedCount: z.number().int(),
  lastDismissedAt: IsoDateTimeSchema.nullable(),
  lastDismissReason: z.string().nullable(),
});
export type DefaultItemRequestGroup = z.infer<typeof DefaultItemRequestGroupSchema>;

export const DefaultItemRequestListQuerySchema = z.object({
  status: z.enum(DEFAULT_ITEM_REQUEST_QUEUE_STATUSES).default('pending'),
  q: z.string().trim().max(100).optional(),
  after: z.string().trim().min(1).max(200).optional(),
  cursor: z.string().trim().min(1).max(200).optional(),
  limit: CursorPageQuerySchema.shape.limit,
});
export type DefaultItemRequestListQuery = z.infer<typeof DefaultItemRequestListQuerySchema>;

export const DefaultItemRequestListResponseSchema = z.object({
  items: z.array(DefaultItemRequestGroupSchema),
  nextCursor: z.string().nullable(),
});
export type DefaultItemRequestListResponse = z.infer<typeof DefaultItemRequestListResponseSchema>;

export const DefaultItemRequestGroupQuerySchema = z.object({
  nameKey: requiredText(200, 'کلید نام را بنویسید.'),
});
export type DefaultItemRequestGroupQuery = z.infer<typeof DefaultItemRequestGroupQuerySchema>;

export const DefaultItemRequestGtinStatusSchema = z.enum(['approved', 'pending', 'rejected', 'unknown']);
export type DefaultItemRequestGtinStatus = z.infer<typeof DefaultItemRequestGtinStatusSchema>;

export const DefaultItemRequestSchema = z.object({
  id: UuidSchema,
  status: z.enum(DEFAULT_ITEM_REQUEST_STATUSES),
  source: z.enum(DEFAULT_ITEM_REQUEST_SOURCES),
  nameFa: z.string(),
  nameEn: z.string().nullable(),
  categoryText: z.string().nullable(),
  unit: z.string(),
  gtin: z.string().nullable(),
  gtinStatus: DefaultItemRequestGtinStatusSchema.nullable(),
  gtinProductId: UuidSchema.nullable(),
  defaultItemId: UuidSchema.nullable(),
  dismissReason: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  decidedAt: IsoDateTimeSchema.nullable(),
  clinic: z.object({ id: UuidSchema, name: z.string() }).nullable(),
});
export type DefaultItemRequest = z.infer<typeof DefaultItemRequestSchema>;

export const DefaultItemRequestGtinInfoSchema = z.object({
  gtin: z.string(),
  status: DefaultItemRequestGtinStatusSchema,
  productId: UuidSchema.nullable(),
});
export type DefaultItemRequestGtinInfo = z.infer<typeof DefaultItemRequestGtinInfoSchema>;

export const DefaultItemRequestGroupDetailSchema = z.object({
  nameKey: z.string(),
  dismissed: z.number().int().optional(),
  requests: z.array(DefaultItemRequestSchema),
  gtins: z.array(DefaultItemRequestGtinInfoSchema),
  history: z.array(DefaultItemEventSchema),
});
export type DefaultItemRequestGroupDetail = z.infer<typeof DefaultItemRequestGroupDetailSchema>;

export const DefaultItemRequestAddInputSchema = DefaultItemInputSchema.extend({
  nameKey: requiredText(200, 'کلید نام را بنویسید.'),
});
export type DefaultItemRequestAddInput = z.input<typeof DefaultItemRequestAddInputSchema>;

export const DefaultItemRequestDismissInputSchema = z.object({
  nameKey: requiredText(200, 'کلید نام را بنویسید.'),
  reason: optionalText(300).refine((v) => v === null || v.length >= 3, {
    message: 'دلیل دست‌کم ۳ نویسه است.',
  }),
});
export type DefaultItemRequestDismissInput = z.input<typeof DefaultItemRequestDismissInputSchema>;
